import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Typography } from "antd";
import { SbTable, SbTag, SbInput, SbButton, SbSwitch, SbTooltip, SbNotification, TooltipTriggerStyled, SearchOutlined, ArrowLeftOutlined, SbBetaTag } from "uiCore";
import { FilterOutlined } from "@ant-design/icons";
import { getAllAgents } from "Api/apiService";
import usePAICStore from "Store/usePAICStore";
import useEntitlementsStore from "Store/useEntitlementsStore";
import { PAIC_ROUTES, buildJdPrdRoute } from "constants/paicRoutes.constants";
import {
  AGENT_STATUS_TAG_COLORS,
  AGENT_SOURCE_TO_PROJECT_SOURCE,
  PAIC_PROJECT_SOURCE,
  PAIC_DOCUMENT_TYPES,
  PAIC_ERROR_MESSAGES,
  handlePAICErrorCode,
} from "constants/peoplePlusAI.constants";
import {
  PageContainer,
  HeaderSection,
  HeaderTopRow,
  TitleGroup,
  SearchWrapper,
  SearchIconBox,
  TableSection,
  GroupTag,
  DescriptionText,
  ClickableRowTableWrapper,
  TitleRow,
  ViewToggle,
  ViewByLabel,
  ViewByText,
} from "./SbAgentLibraryV2.styles";
import SbAgentGroupTableV2 from "./SbAgentGroupTableV2";
import { buildFinancialColumns, buildPrdColumn, formatVersions } from "./agentLibrary.utils";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Title, Text } = Typography;

const resolveProjectSource = (source) => {
  const key = source
    ? source.charAt(0).toUpperCase() + source.slice(1).toLowerCase()
    : "";
  return AGENT_SOURCE_TO_PROJECT_SOURCE[key] || PAIC_PROJECT_SOURCE.SHARED;
};
// Columns live inside the component so each filter dropdown's options come from the loaded agents.

const ANALYTICS_FROM = "paic_agent_library";

const SbAgentLibraryV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  useEffect(() => {
    track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.AGENT_LIBRARY_VIEWED);
  }, [track]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [viewByGroup, setViewByGroup] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(location.state?.page || 1);
  const setProjectId = usePAICStore((state) => state.setProjectId);
  const setGroupName = usePAICStore((state) => state.setGroupName);
  const setProjectSource = usePAICStore((state) => state.setProjectSource);
  const setSelectionId = usePAICStore((state) => state.setSelectionId);
  const setSelectedVersionNumber = usePAICStore((state) => state.setSelectedVersionNumber);
  const setSelectedVersionWithAgents = usePAICStore((state) => state.setSelectedVersionWithAgents);
  const setDocumentType = usePAICStore((state) => state.setDocumentType);
  const setSelectedDocumentAgentId = usePAICStore((state) => state.setSelectedDocumentAgentId);

  // Potential Value is a compensation figure — gate it like PAIC: only roles with the
  // view_comp entitlement on people_plus_ai see it (HR Manager yes, HR Executive no).
  const entitlements = useEntitlementsStore((s) => s.entitlements);
  const paiEntitlements = entitlements?.find((e) => e.name === "people_plus_ai")?.entitlements;
  const canViewPotentialValue = !!paiEntitlements?.includes("view_comp");

  // Open the group page for the clicked agent's group (same store context the dashboard sets).
  const openGroup = useCallback(
    (record) => {
      setProjectId(record.project_id);
      setGroupName(record.group_name);
      setProjectSource(resolveProjectSource(record.source));
      setSelectionId(record.selection_id);
      setSelectedVersionNumber(record.version);
      navigate(PAIC_ROUTES.GROUP_AGENTS, {
        state: { fromAgentLibrary: true, agentLibraryPage: currentPage },
      });
    },
    [
      navigate,
      currentPage,
      setProjectId,
      setGroupName,
      setProjectSource,
      setSelectionId,
      setSelectedVersionNumber,
    ],
  );

  // Open the PRD document view for this specific agent. The PRD page fetches by
  // agent_unique_id (== record.agent_id) from selectedVersionWithAgents, so we seed
  // the store with just this agent to guarantee its PRD is the one shown.
  const openPrd = useCallback(
    (record) => {
      if (!record.prd_generated) return;
      setProjectId(record.project_id);
      setGroupName(record.group_name);
      setProjectSource(resolveProjectSource(record.source));
      // Never-selected agents have no selection, so leave that context untouched.
      if (record.selection_id) {
        setSelectionId(record.selection_id);
        setSelectedVersionNumber(record.version);
      }
      setSelectedVersionWithAgents({
        sb_selection_unique_id: record.selection_id,
        version: record.version,
        agent_unique_ids: [record.agent_id],
      });
      setDocumentType(PAIC_DOCUMENT_TYPES.AGENT_PRD);
      setSelectedDocumentAgentId(record.agent_id);
      navigate(buildJdPrdRoute(PAIC_DOCUMENT_TYPES.AGENT_PRD));
    },
    [
      navigate,
      setProjectId,
      setGroupName,
      setProjectSource,
      setSelectionId,
      setSelectedVersionNumber,
      setSelectedVersionWithAgents,
      setDocumentType,
      setSelectedDocumentAgentId,
    ],
  );

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const data = await getAllAgents();
        if (active) setAgents(Array.isArray(data) ? data : []);
      } catch (error) {
        if (active) {
          setAgents([]);
          if (!handlePAICErrorCode(error, SbNotification.error)) {
            SbNotification.error(
              PAIC_ERROR_MESSAGES.LOAD_AGENTS_FAILED,
              error?.response?.status === 403
                ? "You do not have access to the Agent Library."
                : "Unable to load the Agent Library. Please try again.",
            );
          }
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const filteredAgents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return agents;
    return agents.filter((a) =>
      [a.agent_name, a.agent_code, a.description, a.group_name, a.status, a.source, formatVersions(a)]
        .some((field) => (field ?? "").toString().toLowerCase().includes(term)),
    );
  }, [agents, search]);

  const columns = useMemo(() => {
    const getFilters = (key) =>
      [...new Set(agents.map((a) => a[key]).filter(Boolean))].map((value) => ({
        text: String(value),
        value,
      }));

    return [
      {
        title: "Agent",
        dataIndex: "agent_name",
        key: "agent_name",
        render: (name) => <Text strong>{name || "-"}</Text>,
      },
      {
        title: "Group",
        dataIndex: "group_name",
        key: "group_name",
        width: 180,
        className: "agent-library-group-cell",
        filters: getFilters("group_name"),
        filterSearch: true,
        onFilter: (value, record) => record.group_name === value,
        render: (group, record) =>
          group ? (
            <SbTooltip content="Go to group page">
              <GroupTag
                onClick={(e) => {
                  e.stopPropagation();
                  openGroup(record);
                }}
              >
                <SbTag color="blue">{group}</SbTag>
              </GroupTag>
            </SbTooltip>
          ) : (
            "-"
          ),
      },
      {
        title: "Version",
        dataIndex: "version",
        key: "version",
        width: 120,
        filters: getFilters("version"),
        onFilter: (value, record) => (record.versions || []).includes(value),
        render: (_, record) => formatVersions(record),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        filters: getFilters("status"),
        onFilter: (value, record) => record.status === value,
        render: (status) =>
          status ? (
            <SbTag color={AGENT_STATUS_TAG_COLORS[status] || "default"}>{status}</SbTag>
          ) : (
            "-"
          ),
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        width: 320,
        render: (text) =>
          text ? (
            <DescriptionText ellipsis={{ rows: 3, tooltip: text }}>{text}</DescriptionText>
          ) : (
            "-"
          ),
      },
      {
        title: "Roles Impacted",
        dataIndex: "roles_impacted",
        key: "roles_impacted",
        align: "center",
        render: (v) => v ?? 0,
      },
      ...buildFinancialColumns(canViewPotentialValue),
      buildPrdColumn(openPrd),
    ];
  }, [agents, openGroup, openPrd, canViewPotentialValue]);

  return (
    <PageContainer>
      <HeaderSection>
        <HeaderTopRow>
          <TitleGroup>
            <TitleRow>
              <SbTooltip content="Back to People + AI">
                <TooltipTriggerStyled>
                  <SbButton buttonType="secondary" onClick={() => navigate(PAIC_ROUTES.MAIN)}>
                    <ArrowLeftOutlined />
                  </SbButton>
                </TooltipTriggerStyled>
              </SbTooltip>
              <Title level={4}>Agent Library<SbBetaTag /></Title>
              <ViewToggle>
                <ViewByLabel>View By:</ViewByLabel>
                <ViewByText $active={!viewByGroup}>Agents</ViewByText>
                <SbSwitch checked={viewByGroup} onChange={setViewByGroup} />
                <ViewByText $active={viewByGroup}>Groups</ViewByText>
              </ViewToggle>
            </TitleRow>
            <Text type="secondary">
              Every AI agent across all your groups and versions, in one place — browse
              descriptions, see the roles each agent impacts, and open its documents.
            </Text>
          </TitleGroup>

          <SearchWrapper>
            <SbInput
              placeholder="Search agents, groups, versions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              suffix={
                <SearchIconBox>
                  <SearchOutlined />
                </SearchIconBox>
              }
            />
          </SearchWrapper>
        </HeaderTopRow>
      </HeaderSection>

      <TableSection>
        {viewByGroup ? (
          <SbAgentGroupTableV2
            agents={filteredAgents}
            loading={loading}
            openPrd={openPrd}
            canViewPotentialValue={canViewPotentialValue}
          />
        ) : (
          <ClickableRowTableWrapper>
            <SbTable
              columns={columns}
              dataSource={filteredAgents}
              loading={loading}
              rowKey={(record) => `${record.project_id}:${record.agent_id}`}
              onRow={(record) => ({ onClick: () => openPrd(record) })}
              sticky
              pagination={{
                current: currentPage,
                onChange: (page) => setCurrentPage(page),
                defaultPageSize: 10,
                showSizeChanger: false,
                showTotal: (total) => (
                  <div className="paic-pagination-total">
                    <FilterOutlined />
                    <span className="is-active-text">
                      {total} {total === 1 ? "result" : "results"}
                    </span>
                  </div>
                ),
              }}
            />
          </ClickableRowTableWrapper>
        )}
      </TableSection>
    </PageContainer>
  );
};

export default SbAgentLibraryV2;
